<template>
  <div class="d-flex justify-content-between align-items-center">
    <div class="form-label">{{ $t('campaigns.google_ads.headlines') }}</div>
    <small v-if="maxHeadlines" class="text-muted">
      {{ $t('campaigns.google_ads.headline_count', { current: headlines.length, max: maxHeadlines }) }}
    </small>
  </div>
  <div class="d-flex flex-column gap-2">
    <template v-for="(headline, index) in headlines" :key="index">
      <div class="d-flex gap-2 align-items-center">
        <LeadInput
          v-bind="$attrs"
          :modelValue="headline"
          @update:modelValue="updateHeadline(index, $event)"
          class="flex-grow-1"
          :label="`#${index + 1}`" />
        <LeadButton
          v-if="headlines.length > 1"
          variant="white"
          @click="headlines = headlines.filter((_, i) => i !== index)"
          class="align-self-end">
          <template #prepend-icon>
            <IconTrashFilled size="16" />
          </template>
        </LeadButton>
      </div>
    </template>
    <LeadButton variant="secondary" class="float-right" :disabled="!canAddHeadline" @click="addHeadline">
      <template #prepend-icon>
        <IconPlus size="16" />
      </template>
    </LeadButton>
  </div>

  <div v-if="props.errors && Object.keys(props.errors).length" class="mt-2">
    <FormErrorList :errors="Object.values(props.errors).flat()" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LeadInput from './LeadInput.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconPlus, IconTrashFilled } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import type { TextAssetOptions } from '@/types/types';

const props = withDefaults(
  defineProps<{
    modelValue: string | null | undefined;
    options?: TextAssetOptions;
    errors?: Record<string, string | string[]>;
  }>(),
  {
    modelValue: '',
    options: () => ({}),
    errors: () => ({}),
  }
);

const emit = defineEmits(['update:modelValue']);

// modelValue is separated by \n - multiple headlines
const headlines = computed({
  get: (): string[] => {
    if (!props.modelValue) {
      return [''];
    }
    const lines = props.modelValue.split('\n');
    return lines.length === 0 ? [''] : lines;
  },
  set: (newList: string[]) => {
    // Join the array back into a single string, preserving empty lines
    const newValue = newList.join('\n');
    emit('update:modelValue', newValue);
  },
});
const maxHeadlines = computed(() => props.options?.max_lines);
const canAddHeadline = computed(() => {
  return !maxHeadlines.value || headlines.value.length < maxHeadlines.value;
});

/**
 * Updates a specific headline in the list based on its index.
 */
const updateHeadline = (index: number, newValue: string) => {
  const updatedList = [...headlines.value];
  updatedList[index] = newValue;
  headlines.value = updatedList;
};

const addHeadline = () => {
  if (!canAddHeadline.value) return;
  headlines.value = [...headlines.value, ''];
};
</script>

<style scoped>
:deep(.form-label) {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.float-right {
  float: right;
}
</style>
